<template>
  <div class="min-h-screen bg-background flex items-center justify-center px-4 py-12">
    <div class="max-w-md w-full">
      <div class="text-center mb-8">
        <router-link to="/" class="inline-block">
          <div class="h-12 w-48 overflow-hidden rounded-lg mx-auto mb-4">
            <img
              src="/images/bi-logo.jpg"
              alt="Brand Integrated"
              class="h-full w-full object-contain object-center"
            />
          </div>
        </router-link>
        <h1 class="text-3xl font-display font-medium text-on-background mb-2">
          Forgot Password
        </h1>
        <p class="text-on-surface-variant">
          Enter your email to receive a password reset link
        </p>
      </div>

      <div class="bg-surface rounded-xl shadow-lg border border-outline-variant/30 p-8">
        <div
          v-if="success"
          class="bg-primary/10 border border-primary/30 text-primary px-4 py-3 rounded-lg text-sm mb-6 font-medium"
        >
          {{ success }}
        </div>

        <form v-if="!success" @submit.prevent="handleForgotPassword" class="space-y-6">
          <div
            v-if="error"
            class="bg-error-container border border-error/30 text-error px-4 py-3 rounded-lg text-sm font-medium"
          >
            {{ error }}
          </div>

          <div>
            <label
              for="email"
              class="block text-sm font-medium text-on-background mb-2"
            >
              Email
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              class="w-full px-4 py-3 rounded-lg border border-outline-variant bg-surface text-on-background placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              placeholder="admin@brandintegrated.com"
              :disabled="isLoading"
            />
          </div>

          <button
            type="submit"
            :disabled="isLoading"
            class="w-full bg-primary hover:bg-primary-container text-on-primary font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative"
          >
            <span v-if="!isLoading">Send Reset Link</span>
            <span v-else class="flex items-center justify-center gap-2">
              <span>Sending...</span>
              <div class="spinner"></div>
            </span>
          </button>
        </form>
      </div>

      <div class="text-center mt-6 space-y-2">
        <router-link
          to="/admin/login"
          class="block text-sm text-on-surface-variant hover:text-primary transition-colors"
        >
          ← Back to Login
        </router-link>
        <router-link
          to="/"
          class="block text-sm text-on-surface-variant hover:text-primary transition-colors"
        >
          ← Back to Home
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { authService } from '../../utils/auth.js';

const router = useRouter();

const email = ref('');
const error = ref('');
const success = ref('');
const isLoading = ref(false);

const handleForgotPassword = async () => {
  error.value = '';
  success.value = '';
  isLoading.value = true;

  try {
    const result = await authService.forgotPassword(email.value);
    
    if (result.success) {
      success.value = result.message;
      if (process.env.NODE_ENV === 'development') {
        setTimeout(() => {
          const resetData = sessionStorage.getItem('password_reset_token');
          if (resetData) {
            const data = JSON.parse(resetData);
            router.push(`/admin/reset-password?token=${encodeURIComponent(data.token)}`);
          }
        }, 2000);
      }
    } else {
      error.value = result.error || 'Failed to send reset link. Please try again.';
    }
  } catch (err) {
    error.value = 'An error occurred. Please try again.';
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
